import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

export interface AIChatModeConfig {
  // Core AI Configuration
  aiPersonality: {
    name: string;
    role: string;
    description: string;
  };
  
  // Conversation Behavior
  conversationStyle: {
    tone: 'professional' | 'friendly' | 'casual' | 'formal';
    responseLength: 'concise' | 'detailed' | 'adaptive';
    proactiveness: 'reactive' | 'proactive' | 'balanced';
  };
  
  // Instructions & Guidelines
  systemInstructions: string;
  conversationGuidelines: string[];
  prohibitedTopics: string[];
  
  // Data Collection
  collectUserInfo: boolean;
  requiredFields: Array<string | { id?: string; name: string; description: string; }>;
  optionalFields: Array<string | { id?: string; name: string; description: string; }>;
  
  // Conversation Management
  welcomeMessage: string;
  fallbackMessage: string;
  endConversationTriggers: string[];
  
  // Multi-Offer Support
  offers: {
    enabled: boolean;
    categories: Array<{
      id: string;
      name: string;
      description: string;
      keywords: string[];
      requiredFields: Array<string | { id?: string; name: string; description: string; }>;
      optionalFields: Array<string | { id?: string; name: string; description: string; }>;
      customInstructions?: string;
    }>;
  };
  
  // Advanced Settings
  contextMemory: boolean;
  maxConversationLength: number;
  escalationTriggers: string[];
}

// Default configuration
const DEFAULT_CONFIG: AIChatModeConfig = {
  aiPersonality: {
    name: 'AI Assistant',
    role: 'Customer Service Representative',
    description: 'A helpful and knowledgeable AI assistant focused on providing excellent customer service and support.'
  },
  
  conversationStyle: {
    tone: 'professional',
    responseLength: 'adaptive',
    proactiveness: 'balanced'
  },
  
  systemInstructions: `You are a helpful AI assistant representing this company. Your goal is to:

1. Provide accurate and helpful information
2. Maintain a professional and friendly tone
3. Ask clarifying questions when needed
4. Guide users toward solutions
5. Escalate to human support when appropriate

Always be respectful, patient, and focused on helping the user achieve their goals.`,

  conversationGuidelines: [
    'Always greet users warmly and professionally',
    'Ask clarifying questions to better understand user needs',
    'Provide specific and actionable advice',
    'Confirm understanding before providing solutions',
    'Offer additional help before ending conversations'
  ],
  
  prohibitedTopics: [
    'Personal financial advice',
    'Medical or health advice',
    'Legal advice',
    'Political opinions'
  ],
  
  collectUserInfo: true,
  requiredFields: [
    { name: 'name', description: 'I need to know your name' },
    { name: 'email', description: 'I need your email address to contact you' }
  ],
  optionalFields: [
    { name: 'company', description: 'What company do you work for?' },
    { name: 'phone', description: 'Your phone number for follow-up calls' }
  ],
  
  welcomeMessage: 'Hello! I\'m here to help you with any questions or concerns you may have. How can I assist you today?',
  fallbackMessage: 'I apologize, but I didn\'t quite understand that. Could you please rephrase your question or provide more details?',
  endConversationTriggers: ['goodbye', 'bye', 'end chat', 'no thanks', 'that\'s all'],
  
  // Multi-Offer Default Configuration
  offers: {
    enabled: false,
    categories: [
      {
        id: 'insurance',
        name: 'Insurance',
        description: 'Life, health, auto, and home insurance products',
        keywords: ['insurance', 'coverage', 'policy', 'premium', 'deductible', 'claim'],
        requiredFields: [
          { name: 'name', description: 'Your full name' },
          { name: 'email', description: 'Your email address' },
          { name: 'phone', description: 'Your phone number' }
        ],
        optionalFields: [
          { name: 'age', description: 'Your age for insurance quotes' },
          { name: 'current_coverage', description: 'Do you have existing insurance?' }
        ],
        customInstructions: 'Focus on understanding their insurance needs and provide relevant information about coverage options.'
      },
      {
        id: 'travel',
        name: 'Travel & Cruises',
        description: 'Travel packages, cruises, and vacation deals',
        keywords: ['travel', 'cruise', 'vacation', 'trip', 'destination', 'booking'],
        requiredFields: [
          { name: 'name', description: 'Your full name' },
          { name: 'email', description: 'Your email address' }
        ],
        optionalFields: [
          { name: 'travel_dates', description: 'When are you planning to travel?' },
          { name: 'destination', description: 'Where would you like to go?' },
          { name: 'budget', description: 'What\'s your approximate budget?' }
        ],
        customInstructions: 'Help them explore travel options and find the perfect vacation package.'
      },
      {
        id: 'mortgage',
        name: 'Mortgage Relief',
        description: 'Mortgage assistance, refinancing, and relief programs',
        keywords: ['mortgage', 'refinance', 'loan', 'payment', 'relief', 'foreclosure'],
        requiredFields: [
          { name: 'name', description: 'Your full name' },
          { name: 'email', description: 'Your email address' },
          { name: 'phone', description: 'Your phone number' }
        ],
        optionalFields: [
          { name: 'current_payment', description: 'Your current monthly payment' },
          { name: 'loan_balance', description: 'Remaining loan balance' },
          { name: 'property_value', description: 'Estimated property value' }
        ],
        customInstructions: 'Understand their mortgage situation and guide them toward appropriate relief options.'
      }
    ],

  },
  
  contextMemory: true,
  maxConversationLength: 50,
  escalationTriggers: ['speak to human', 'human agent', 'manager', 'supervisor', 'complaint']
};

export const useAIChatModeConfig = (chatbotId: string) => {
  const [config, setConfig] = useState<AIChatModeConfig>(DEFAULT_CONFIG);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  // Load configuration from database
  const loadConfig = useCallback(async () => {
    if (!chatbotId) {
      console.log('🔍 AI CONFIG LOAD DEBUG - No chatbotId provided');
      return;
    }

    console.log('🔍 AI CONFIG LOAD DEBUG - Starting load for chatbot:', chatbotId);
    setIsLoading(true);
    try {
      const { data: chatbot, error } = await supabase
        .from('chatbots')
        .select('ai_settings, settings')
        .eq('id', chatbotId)
        .single();

      console.log('🔍 AI CONFIG LOAD DEBUG - Database response:', {
        error: error?.message,
        hasData: !!chatbot,
        hasAiSettings: !!chatbot?.ai_settings,
        hasSettings: !!chatbot?.settings,
        aiSettingsType: typeof chatbot?.ai_settings,
        settingsType: typeof chatbot?.settings
      });

      if (error) {
        console.error('Error loading AI chat config:', error);
        toast({
          title: "Error loading configuration",
          description: "Using default settings",
          variant: "destructive"
        });
        return;
      }

      // Check both ai_settings and settings.ai_chat_config for backward compatibility
      let aiChatConfig = null;

      if ((chatbot as any)?.ai_settings && (chatbot as any).ai_settings.ai_chat_config) {
        aiChatConfig = (chatbot as any).ai_settings.ai_chat_config;
        console.log('🔍 AI CONFIG LOAD DEBUG - Found config in ai_settings.ai_chat_config');
      } else if ((chatbot as any)?.settings && (chatbot as any).settings.ai_chat_config) {
        aiChatConfig = (chatbot as any).settings.ai_chat_config;
        console.log('🔍 AI CONFIG LOAD DEBUG - Found config in settings.ai_chat_config');
      }

      console.log('🔍 AI CONFIG LOAD DEBUG - Extracted config:', {
        foundConfig: !!aiChatConfig,
        configKeys: aiChatConfig ? Object.keys(aiChatConfig) : [],
        hasPersonality: !!aiChatConfig?.aiPersonality,
        personalityName: aiChatConfig?.aiPersonality?.name,
        collectUserInfo: aiChatConfig?.collectUserInfo,
        requiredFieldsCount: aiChatConfig?.requiredFields?.length || 0,
        multiOfferEnabled: aiChatConfig?.offers?.enabled,
        categoriesCount: aiChatConfig?.offers?.categories?.length || 0
      });

      if (aiChatConfig) {
        // Merge with defaults to ensure all properties exist
        const mergedConfig = {
          ...DEFAULT_CONFIG,
          ...aiChatConfig
        };
        setConfig(mergedConfig);
        console.log('🔍 AI CONFIG LOAD DEBUG - Config loaded and merged with defaults:', {
          collectUserInfo: mergedConfig.collectUserInfo,
          requiredFieldsCount: mergedConfig.requiredFields?.length || 0,
          multiOfferEnabled: mergedConfig.offers?.enabled
        });
      } else {
        console.log('🔍 AI CONFIG LOAD DEBUG - No AI chat config found, using defaults');
        setConfig(DEFAULT_CONFIG);
      }
    } catch (error) {
      console.error('Error loading AI chat config:', error);
      toast({
        title: "Error loading configuration",
        description: "Using default settings",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  }, [chatbotId, toast]);

  // Clean configuration data to prevent circular references
  const cleanConfigForSaving = (configData: AIChatModeConfig): AIChatModeConfig => {
    return {
      // AI Personality
      aiPersonality: {
        name: String(configData.aiPersonality?.name || ''),
        role: String(configData.aiPersonality?.role || ''),
        description: String(configData.aiPersonality?.description || '')
      },
      conversationStyle: {
        tone: (configData.conversationStyle?.tone as 'professional' | 'friendly' | 'casual' | 'formal') || 'professional',
        responseLength: (configData.conversationStyle?.responseLength as 'concise' | 'detailed' | 'adaptive') || 'adaptive',
        proactiveness: (configData.conversationStyle?.proactiveness as 'reactive' | 'proactive' | 'balanced') || 'balanced'
      },

      // System Instructions
      systemInstructions: String(configData.systemInstructions || ''),
      conversationGuidelines: Array.isArray(configData.conversationGuidelines)
        ? configData.conversationGuidelines.map(g => String(g)).filter(g => g.trim())
        : [],
      prohibitedTopics: Array.isArray(configData.prohibitedTopics)
        ? configData.prohibitedTopics.map(t => String(t)).filter(t => t.trim())
        : [],

      // Data Collection
      collectUserInfo: Boolean(configData.collectUserInfo),
      requiredFields: Array.isArray(configData.requiredFields)
        ? configData.requiredFields.map(field =>
            typeof field === 'string'
              ? { name: String(field), description: '' }
              : { name: String(field.name || ''), description: String(field.description || '') }
          ).filter(f => f.name.trim())
        : [],
      optionalFields: Array.isArray(configData.optionalFields)
        ? configData.optionalFields.map(field =>
            typeof field === 'string'
              ? { name: String(field), description: '' }
              : { name: String(field.name || ''), description: String(field.description || '') }
          ).filter(f => f.name.trim())
        : [],

      // Conversation Management
      welcomeMessage: String(configData.welcomeMessage || ''),
      fallbackMessage: String(configData.fallbackMessage || ''),

      // Multi-Offer Support
      offers: configData.offers || {
        enabled: false,
        categories: []
      },

      // Advanced Settings
      contextMemory: Boolean(configData.contextMemory),
      maxConversationLength: Number(configData.maxConversationLength) || 50,
      escalationTriggers: Array.isArray(configData.escalationTriggers)
        ? configData.escalationTriggers.map(t => String(t)).filter(t => t.trim())
        : [],
      endConversationTriggers: Array.isArray(configData.endConversationTriggers)
        ? configData.endConversationTriggers.map(t => String(t)).filter(t => t.trim())
        : []
    };
  };

  // Save configuration to database
  const saveConfig = useCallback(async (newConfig?: AIChatModeConfig) => {
    if (!chatbotId) return;

    const rawConfig = newConfig || config;
    const configToSave = cleanConfigForSaving(rawConfig);

    // Debug log to verify clean data
    console.log('Saving cleaned AI config:', {
      hasPersonality: !!configToSave.aiPersonality?.name,
      hasInstructions: !!configToSave.systemInstructions,
      collectsData: configToSave.collectUserInfo,
      requiredFieldsCount: configToSave.requiredFields.length,
      optionalFieldsCount: configToSave.optionalFields.length
    });

    setIsSaving(true);

    try {
      // First, get current settings (not ai_settings)
      const { data: chatbot, error: fetchError } = await supabase
        .from('chatbots')
        .select('settings')
        .eq('id', chatbotId)
        .single();

      console.log('🔍 AI CONFIG SAVE DEBUG - Current settings fetch:', {
        error: fetchError?.message,
        hasSettings: !!chatbot?.settings,
        settingsType: typeof chatbot?.settings
      });

      if (fetchError) {
        throw fetchError;
      }

      // Parse settings if it's a string
      let currentSettings = chatbot?.settings || {};
      if (typeof currentSettings === 'string') {
        try {
          currentSettings = JSON.parse(currentSettings);
        } catch (e) {
          console.error('Error parsing settings:', e);
          currentSettings = {};
        }
      }

      // Merge AI chat config with existing settings
      const updatedSettings = {
        ...(typeof currentSettings === 'object' ? currentSettings : {}),
        ai_chat_config: JSON.parse(JSON.stringify(configToSave)) // Ensure serializable
      };

      console.log('🔍 AI CONFIG SAVE DEBUG - Saving to settings field:', {
        currentSettingsKeys: Object.keys(currentSettings),
        updatedSettingsKeys: Object.keys(updatedSettings),
        aiConfigKeys: Object.keys(configToSave),
        collectUserInfo: configToSave.collectUserInfo,
        requiredFieldsCount: configToSave.requiredFields?.length || 0
      });

      // Update the chatbot settings (not ai_settings)
      const { data: updateData, error: updateError } = await supabase
        .from('chatbots')
        .update({ settings: updatedSettings as any }) // Type assertion for Json compatibility
        .eq('id', chatbotId)
        .select();

      if (updateError) {
        throw updateError;
      }

      console.log('AI config save result:', {
        chatbotId,
        updatedRows: updateData?.length || 0,
        configKeys: Object.keys(configToSave)
      });

      toast({
        title: "Configuration saved",
        description: "AI chat mode settings have been updated successfully"
      });

      return true;
    } catch (error) {
      console.error('Error saving AI chat config:', error);
      toast({
        title: "Error saving configuration",
        description: "Please try again",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [chatbotId, config, toast]);

  // Update configuration
  const updateConfig = useCallback((updates: Partial<AIChatModeConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  }, []);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    setConfig(DEFAULT_CONFIG);
    toast({
      title: "Configuration reset",
      description: "Settings have been reset to defaults"
    });
  }, [toast]);

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  return {
    config,
    updateConfig,
    saveConfig,
    resetToDefaults,
    isLoading,
    isSaving,
    reload: loadConfig
  };
};

// Helper function to generate system prompt from config
export const generateSystemPrompt = (config: AIChatModeConfig): string => {
  let prompt = `You are ${config.aiPersonality.name}, a ${config.aiPersonality.role}.

${config.aiPersonality.description}

${config.systemInstructions}

Conversation Style:
- Tone: ${config.conversationStyle.tone}
- Response Length: ${config.conversationStyle.responseLength}
- Approach: ${config.conversationStyle.proactiveness}

IMPORTANT: Keep your responses conversational and under 150 characters when possible. Use short, friendly messages that feel natural and engaging.

PRIMARY OBJECTIVE: Your FIRST PRIORITY is to collect ALL required information fields before addressing any other user needs or questions. This is critical for providing proper service.

Guidelines:
${config.conversationGuidelines.map(guideline => `- ${guideline}`).join('\n')}`;

  if (config.prohibitedTopics.length > 0) {
    prompt += `\n\nProhibited Topics (politely decline to discuss):
${config.prohibitedTopics.map(topic => `- ${topic}`).join('\n')}`;
  }

  if (config.collectUserInfo) {
    const requiredFieldsText = config.requiredFields
      .map(field => typeof field === 'string' ? field : `${field.name} (${field.description})`)
      .join(', ');
    const optionalFieldsText = config.optionalFields
      .map(field => typeof field === 'string' ? field : `${field.name} (${field.description})`)
      .join(', ');

    prompt += `\n\nCRITICAL DATA COLLECTION INSTRUCTIONS:
    
REQUIRED FIELDS (MUST COLLECT):
${config.requiredFields.map((field, index) => {
  const fieldData = typeof field === 'string' ? { name: field, description: field } : field;
  return `${index + 1}. ${fieldData.name}: ${fieldData.description || 'Required information'}`;
}).join('\n')}

OPTIONAL FIELDS (collect if relevant):
${config.optionalFields.length > 0 ? config.optionalFields.map((field) => {
  const fieldData = typeof field === 'string' ? { name: field, description: field } : field;
  return `- ${fieldData.name}: ${fieldData.description || 'Optional information'}`;
}).join('\n') : '- None'}

DATA COLLECTION STRATEGY:
1. PRIORITIZE collecting ALL required fields before moving to other topics
2. Ask for required fields naturally in the conversation flow
3. If a user tries to skip providing required information, gently redirect them back
4. Do NOT proceed with main requests until you have ALL required fields
5. Be persistent but polite - explain why you need each piece of required information
6. Track which required fields you've collected and which are still missing
7. If you're missing required fields after initial conversation, summarize what you still need

IMPORTANT: Your primary goal is to collect ALL required fields: ${requiredFieldsText}`;
  }

  if (config.endConversationTriggers.length > 0) {
    prompt += `\n\nEnd conversation when users say: ${config.endConversationTriggers.join(', ')}`;
  }

  if (config.escalationTriggers.length > 0) {
    prompt += `\n\nEscalate to human support when users mention: ${config.escalationTriggers.join(', ')}`;
  }

  // Multi-Offer Support
  if (config.offers.enabled && config.offers.categories.length > 0) {
    prompt += `\n\nMULTI-OFFER CAPABILITIES:
You can help users with multiple types of offers in a single conversation. Available categories:`;
    
    config.offers.categories.forEach(category => {
      prompt += `\n\n${category.name.toUpperCase()}:
- Description: ${category.description}
- Keywords to watch for: ${category.keywords.join(', ')}
- ${category.customInstructions || 'Provide helpful information about this category'}`;
      
      if (category.requiredFields.length > 0) {
        prompt += `\n- REQUIRED FIELDS for ${category.name} (MUST collect ALL before proceeding):`;
        category.requiredFields.forEach((field, index) => {
          const fieldData = typeof field === 'string' ? { name: field, description: field } : field;
          prompt += `\n  ${index + 1}. ${fieldData.name}: ${fieldData.description || 'Required'}`;
        });
      }
      
      if (category.optionalFields.length > 0) {
        prompt += `\n- Optional fields for ${category.name}:`;
        category.optionalFields.forEach(field => {
          const fieldData = typeof field === 'string' ? { name: field, description: field } : field;
          prompt += `\n  • ${fieldData.name}: ${fieldData.description || 'Optional'}`;
        });
      }
      
      prompt += `\n- IMPORTANT: Do NOT process ${category.name} requests until ALL required fields are collected`;
    });
    
    prompt += `\n\nMULTI-OFFER DATA COLLECTION RULES:
1. When a user expresses interest in ANY category, immediately start collecting required fields
2. If interest in multiple categories, collect shared fields first, then category-specific ones
3. Always explain why you need each piece of information
4. Never process requests or provide detailed solutions until ALL required fields are collected
5. Use this format when asking for information: "To help you with [category], I'll need your [field] - [why it's needed]"`;
  }

  return prompt;
};

// Helper function to check if message should end conversation
export const shouldEndConversation = (message: string, config: AIChatModeConfig): boolean => {
  const lowerMessage = message.toLowerCase();
  return config.endConversationTriggers.some(trigger => 
    lowerMessage.includes(trigger.toLowerCase())
  );
};

// Helper function to check if message should escalate
export const shouldEscalate = (message: string, config: AIChatModeConfig): boolean => {
  const lowerMessage = message.toLowerCase();
  return config.escalationTriggers.some(trigger => 
    lowerMessage.includes(trigger.toLowerCase())
  );
};

// Helper function to detect interested offer categories from message
export const detectOfferInterests = (message: string, config: AIChatModeConfig): string[] => {
  if (!config.offers.enabled) return [];
  
  const lowerMessage = message.toLowerCase();
  const interestedCategories: string[] = [];
  
  config.offers.categories.forEach(category => {
    const hasKeyword = category.keywords.some(keyword => 
      lowerMessage.includes(keyword.toLowerCase())
    );
    
    if (hasKeyword) {
      interestedCategories.push(category.id);
    }
  });
  
  return interestedCategories;
};

// Helper function to get category details by ID
export const getCategoryById = (categoryId: string, config: AIChatModeConfig) => {
  return config.offers.categories.find(cat => cat.id === categoryId);
};

// Helper function to get required fields for multiple categories
export const getRequiredFieldsForCategories = (categoryIds: string[], config: AIChatModeConfig) => {
  const allFields = new Map<string, { name: string; description: string; categories: string[] }>();
  
  categoryIds.forEach(categoryId => {
    const category = getCategoryById(categoryId, config);
    if (category) {
      category.requiredFields.forEach(field => {
        const fieldData = typeof field === 'string' ? { name: field, description: field } : field;
        const existing = allFields.get(fieldData.name);
        
        if (existing) {
          existing.categories.push(category.name);
        } else {
          allFields.set(fieldData.name, {
            name: fieldData.name,
            description: fieldData.description,
            categories: [category.name]
          });
        }
      });
    }
  });
  
  return Array.from(allFields.values());
};

// Helper function to get optional fields for multiple categories
export const getOptionalFieldsForCategories = (categoryIds: string[], config: AIChatModeConfig) => {
  const allFields = new Map<string, { name: string; description: string; categories: string[] }>();
  
  categoryIds.forEach(categoryId => {
    const category = getCategoryById(categoryId, config);
    if (category) {
      category.optionalFields.forEach(field => {
        const fieldData = typeof field === 'string' ? { name: field, description: field } : field;
        const existing = allFields.get(fieldData.name);
        
        if (existing) {
          existing.categories.push(category.name);
        } else {
          allFields.set(fieldData.name, {
            name: fieldData.name,
            description: fieldData.description,
            categories: [category.name]
          });
        }
      });
    }
  });
  
  return Array.from(allFields.values());
};

// Helper function to generate contextual response for multiple interests
export const generateMultiOfferResponse = (interestedCategories: string[], config: AIChatModeConfig): string => {
  if (interestedCategories.length === 0) return '';
  
  const categories = interestedCategories.map(id => getCategoryById(id, config)).filter(Boolean);
  
  if (categories.length === 1) {
    return `I see you're interested in ${categories[0]!.name.toLowerCase()}. ${categories[0]!.customInstructions || 'Let me help you with that.'}`;
  }
  
  const categoryNames = categories.map(cat => cat!.name.toLowerCase());
  const lastCategory = categoryNames.pop();
  const otherCategories = categoryNames.join(', ');
  
  return `I see you're interested in ${otherCategories} and ${lastCategory}. I can help you with all of these! Which would you like to start with, or would you like information about all of them?`;
};
